import { html, LitElement, TemplateResult } from "lit";
import {
  customElement,
  property,
  query,
  queryAssignedNodes,
  state,
} from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { SlhNavigationItemType } from "@slhuinav/item/src/item.interface";
import { Translatable } from "@slhuinav/i18n";
import {
  EVENT_CHANGE,
  ACTIVATOR_SLOT,
  ORGANIZATION_COLLAPSE_CLICK,
} from "./list.constants";
import { SlhNavigationListPosition } from "./list.type";
import { style } from "./assets/list.generated-style";

/**
 * Navigation list component
 *
 * @CustomElement slh-nav-list
 *
 * @cssprop --slh-color-neutral-shade-40 - Activator element fill color. List scrollbar color.
 * @cssprop --slh-color-neutral-shade-00 - List background color. Activator element hover fill color.
 * @cssprop --slh-color-neutral-shade-80 - Activator element hover background color.
 * @cssprop --slh-color-outline - Item focus-visible outline color.
 *
 * ** slots
 * @slot default - Contains navigation items. Required.
 * @slot activator - Contains an element which toggles navigation items visibility. Required.
 */
@customElement("slh-nav-list")
export default class SlhNavigationList extends Translatable(LitElement) {
  static styles = style;

  @queryAssignedNodes()
  protected defaultSlotNodes: Array<SlhNavigationItemType> | undefined;

  @queryAssignedNodes({ slot: ACTIVATOR_SLOT })
  protected activatorSlotNodes: Array<HTMLElement> | undefined;

  /**
   * List HTML name attribute
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  name: string = "";

  /**
   * List HTML aria-label attribute
   */
  @property({
    type: String,
    attribute: "aria-label",
  })
  ariaLabel: string = "";

  /**
   * Set list position relative to the activator element ("right" or "bottom")
   * When set to "right", list items are top-aligned with activator element
   * When set to "bottom", list items are right-aligned with activator element
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  position: string = SlhNavigationListPosition.BOTTOM;

  /**
   * List position offset, relative to the position
   * Adds defined offset to top or right position of the list
   */
  @property({
    type: Number,
    attribute: true,
    reflect: true,
  })
  offset: number = 0;

  /**
   * List activator element
   */
  @query(".list-activator") listActivator!: HTMLElement;

  /**
   * List activator container element
   */
  @query(".activator-container")
  activatorContainer!: HTMLElement;

  /**
   * Navigation items container
   */
  @query(".list-items")
  itemsContainer!: HTMLElement;

  @state()
  protected activatorState: string = "";

  @state()
  protected openState: boolean = false;

  protected items: Array<SlhNavigationItemType> = [];

  itemsTopPosition: string = "";

  itemsLeftPosition: string = "";

  focusedOptionIndex?: number = undefined;

  onOutsideClick: (this: Document, e: MouseEvent) => any = () => {};

  onOutsideKeydown: (this: Document, e: KeyboardEvent) => any = () => {};

  onItemClick: (this: Document, e: Event) => any = () => {};

  private onResize: (e: Event) => void = () => {};

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("list-wrapper");
    this.addEventListener("focus", this.handleFocus);
    this.addEventListener("keydown", this.handleKeydown);
    this.onResize = this.adjustItemsContainerPosition.bind(this);
    window.addEventListener("resize", this.onResize);
    setTimeout(() => this.adjustItemsContainerPosition());
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onOutsideClick);
    document.removeEventListener("keydown", this.onOutsideKeydown);
    this.removeEventListener("keydown", this.handleKeydown);
    if (this.items?.length) {
      this.items.forEach((item) => {
        item.removeEventListener("click", this.onItemClick);
      });
    }
    window.removeEventListener("resize", this.onResize);
    super.disconnectedCallback();

    this.querySelector(".organization-collapse")?.removeEventListener(
      ORGANIZATION_COLLAPSE_CLICK,
      this.collapseOrganization.bind(this),
    );
  }

  /**
   * Render list activator element
   * @returns {TemplateResult}
   */
  renderActivator(): TemplateResult {
    return html`<span
      @click="${this.toggle}"
      class="activator-container"
      tabindex="0"
      aria-haspopup="listbox"
      aria-expanded="${ifDefined(this.openState ? "true" : undefined)}"
    >
      <slot name="${ACTIVATOR_SLOT}"></slot>
    </span>`;
  }

  /**
   * Render list navigation items
   * @returns {TemplateResult}
   */
  renderItems(): TemplateResult {
    return html`<div
      class="list-items"
      style="${this.itemsTopPosition} ${this.itemsLeftPosition}"
      role="listbox"
    >
      <slot @slotchange="${this.onSlotChange}"></slot>
    </div>`;
  }

  private onSlotChange() {
    this.querySelector(".organization-collapse")?.addEventListener(
      ORGANIZATION_COLLAPSE_CLICK,
      this.collapseOrganization.bind(this),
    );
  }

  private collapseOrganization() {
    const organizationCollapse = this.querySelector(".organization-collapse");
    const hiddenOrganizationItems = this.querySelectorAll(".hidden-item");

    if (organizationCollapse) {
      hiddenOrganizationItems.forEach((item) => {
        item.classList.toggle("hide");
      });

      organizationCollapse?.classList.toggle("view-all");
      if (organizationCollapse?.classList.contains("view-all")) {
        organizationCollapse.textContent = this.trans(
          "headerbar.organization.view_all",
        );
      } else {
        organizationCollapse.textContent = this.trans(
          "headerbar.organization.view_less",
        );
      }
    }
  }

  /**
   * @returns boolean
   */
  public getOpenState(): boolean {
    return this.openState;
  }

  /**
   * Toggle select
   */
  public toggle() {
    if (this.openState) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Show list items container
   */
  public open() {
    this.onOutsideClick = this.outsideClick.bind(this);
    document.addEventListener("click", this.onOutsideClick);
    this.onOutsideKeydown = this.outsideKeydown.bind(this);
    document.addEventListener("keydown", this.onOutsideKeydown);
    this.openState = true;
  }

  /**
   * Hide list items container
   */
  public close() {
    document.removeEventListener("click", this.onOutsideClick);
    document.removeEventListener("keydown", this.onOutsideKeydown);
    this.focusedOptionIndex = undefined;
    this.openState = false;
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  renderClasses(): ClassInfo {
    return {
      open: this.openState,
    };
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html`<div class="list-container ${classMap(this.renderClasses())}">
      <div class="list-activator">${this.renderActivator()}</div>
      ${this.renderItems()}
    </div>`;
  }

  /**
   * Set label and state from slots and remove non-item slots
   */
  firstUpdated() {
    this.setAriaLabel();

    this.onItemClick = this.itemClick.bind(this);

    this.items = this.querySelectorAll(
      "slh-nav-item",
    ) as unknown as Array<SlhNavigationItemType>;
    this.items.forEach((item) => {
      item.addEventListener("click", this.onItemClick);
    });
  }

  /**
   * Hide list items container on outside click
   */
  private outsideClick(event: MouseEvent): void {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.close();
    }
  }

  /**
   * Hide list items container on outside click
   */
  private outsideKeydown(event: KeyboardEvent): void {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.close();
    }
  }

  /**
   * Focus activator on component focus
   */
  private handleFocus(event: Event): void {
    const node = event.target as HTMLElement;
    if (node.nodeName === "SLH-NAV-LIST") {
      this.activatorContainer?.focus();
    }
  }

  /**
   * Arrow down click handler
   */
  private arrowDown(event: KeyboardEvent): void {
    event.preventDefault();
    let nextOption: number;
    if (!this.openState) {
      this.open();
    }
    if (this.items.length) {
      if (this.focusedOptionIndex === undefined) {
        nextOption = 0;
      } else {
        nextOption = this.focusedOptionIndex + 1;
      }

      if (nextOption >= this.items?.length) {
        nextOption = 0;
      }

      setTimeout(() => this.items[nextOption].focus(), 1);
      this.items[nextOption].focus();
      this.focusedOptionIndex = nextOption;
    }
  }

  /**
   * Arrow up click handler
   */
  private arrowUp(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.openState) {
      this.open();
    }
    let nextOption: number;
    if (this.items.length) {
      if (this.focusedOptionIndex === undefined) {
        nextOption = 0;
      } else {
        nextOption = this.focusedOptionIndex - 1;
      }

      if (nextOption <= -1) {
        nextOption = this.items.length - 1;
      }
      this.items[nextOption].focus();
      this.focusedOptionIndex = nextOption;
    }
  }

  /**
   * Open list on Enter key press
   */
  private openOnEnter(event: Event): void {
    const node = event.target as HTMLElement;
    if (node.nodeName === "SLH-NAV-LIST") {
      this.open();
    }
  }

  /**
   * w3.org keyboard support
   */
  private handleKeydown(event: KeyboardEvent) {
    const { code } = event;
    switch (code) {
      case "Enter":
      case "NumpadEnter":
        this.handleFocus(event);
        this.openOnEnter(event);
        break;
      case "ArrowDown":
        this.arrowDown(event);
        break;
      case "ArrowUp":
        this.arrowUp(event);
        break;
      case "Escape":
        this.close();
        break;
      default:
        break;
    }
  }

  /**
   * Adjust list items container position on screen resize
   */
  private adjustItemsContainerPosition() {
    const activatorRect = this.listActivator.getBoundingClientRect();
    if (this.position === SlhNavigationListPosition.BOTTOM) {
      setTimeout(() => {
        this.itemsTopPosition = `top: ${
          activatorRect.bottom - activatorRect.top + this.offset
        }px;`;
      });
    } else {
      setTimeout(() => {
        this.itemsLeftPosition = `left: ${
          activatorRect.right - activatorRect.left + this.offset
        }px;`;
      });
    }
  }

  /**
   * Item click event handler
   */
  private itemClick(event: Event) {
    event.preventDefault();

    if (!(event instanceof CustomEvent)) {
      return;
    }

    let isActivator = false;
    event.composedPath().forEach((eventTarget: EventTarget) => {
      const element = eventTarget as HTMLElement;
      if (element.slot && element.slot === ACTIVATOR_SLOT) {
        isActivator = true;
      }
    });

    if (isActivator) {
      this.toggle();
    } else {
      this.close();
    }

    const customEvent = new CustomEvent(EVENT_CHANGE, {
      detail: {
        value: event.detail.value,
        id: this.id,
        name: this.name,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }

  private setAriaLabel(): void {
    this.activatorSlotNodes?.forEach((node: HTMLElement) => {
      if (typeof node.textContent?.trim() === "string") {
        this.activatorState = node.textContent.trim();
        this.itemsContainer?.setAttribute(
          "aria-label",
          node.textContent.trim(),
        );
      }
    });

    if (this.ariaLabel !== "") {
      this.itemsContainer?.setAttribute("aria-label", this.ariaLabel);
    }

    this.removeAttribute("aria-label");
  }
}
